/**
 * Permission Management System
 *
 * This module implements the core permission management functionality for the
 * hybrid Clerk + Application-level RBAC system.
 */

import {
  ClerkRole,
  AppRole,
  AppPermission,
  UserRoleContext,
  ROLE_PERMISSIONS,
  AuthorizationError,
  AuthenticationError,
} from "@/types/auth";

/**
 * Get user permissions based on Clerk role, app role, and owner status
 */
export function getUserPermissions(
  clerkRole: ClerkRole,
  appRole: AppRole,
  isOwner: boolean = false,
  roleFlags?: Record<string, boolean>
): AppPermission[] {
  // Owners get all permissions
  if (isOwner) {
    return ["*"] as AppPermission[];
  }

  // Get base permissions from app role
  const basePermissions = ROLE_PERMISSIONS[appRole] || [];

  // Add Clerk-specific permissions
  const clerkPermissions: AppPermission[] = [];
  if (clerkRole === "org:admin") {
    clerkPermissions.push("settings:read", "team:write", "organization:write");
  }

  // Add role flag permissions (for future extensibility)
  const flagPermissions: AppPermission[] = [];
  if (roleFlags) {
    if (roleFlags.billing_access) {
      flagPermissions.push("organization:billing");
    }
    if (roleFlags.audit_access) {
      flagPermissions.push("audit:read");
    }
  }

  // Combine all permissions and remove duplicates
  const allPermissions = [...basePermissions, ...clerkPermissions, ...flagPermissions];
  return Array.from(new Set(allPermissions));
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  userPermissions: AppPermission[],
  requiredPermission: AppPermission
): boolean {
  // Wildcard permission grants everything
  if (userPermissions.includes("*")) {
    return true;
  }

  // Check for exact permission match
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if user has any of the required permissions
 */
export function hasAnyPermission(
  userPermissions: AppPermission[],
  requiredPermissions: AppPermission[]
): boolean {
  return requiredPermissions.some((permission) => hasPermission(userPermissions, permission));
}

/**
 * Check if user has all of the required permissions
 */
export function hasAllPermissions(
  userPermissions: AppPermission[],
  requiredPermissions: AppPermission[]
): boolean {
  return requiredPermissions.every((permission) => hasPermission(userPermissions, permission));
}

/**
 * Create a permission checker function for a specific user
 */
export function createPermissionChecker(
  userPermissions: AppPermission[]
): (permission: AppPermission) => boolean {
  return (permission: AppPermission) => hasPermission(userPermissions, permission);
}

/**
 * Determine app role from Clerk role and additional context
 */
export function determineAppRole(
  clerkRole: ClerkRole,
  isOwner: boolean = false,
  roleFlags?: Record<string, boolean>
): AppRole {
  // Owner always gets owner role regardless of Clerk role
  if (isOwner) {
    return "owner";
  }

  // Check for manager flag
  if (roleFlags?.manager && clerkRole === "org:member") {
    return "manager";
  }

  // Default mapping based on Clerk role
  switch (clerkRole) {
    case "org:admin":
      return "admin";
    case "org:member":
      return "member";
    default:
      return "member";
  }
}

/**
 * Create a complete user role context
 */
export function createUserRoleContext(
  userId: string,
  organizationId: string | undefined,
  clerkRole: ClerkRole,
  isOwner: boolean = false,
  roleFlags?: Record<string, boolean>
): UserRoleContext {
  const appRole = determineAppRole(clerkRole, isOwner, roleFlags);
  const permissions = getUserPermissions(clerkRole, appRole, isOwner, roleFlags);

  return {
    clerkRole,
    appRole,
    permissions,
    isOwner,
    userId,
    organizationId,
    roleFlags,
  };
}

/**
 * Validate that a user has required permission and throw if not
 */
export function requirePermission(
  userPermissions: AppPermission[],
  requiredPermission: AppPermission,
  context?: string
): void {
  if (!hasPermission(userPermissions, requiredPermission)) {
    throw new AuthorizationError(
      `Missing required permission: ${requiredPermission}${context ? ` for ${context}` : ""}`,
      requiredPermission,
      userPermissions
    );
  }
}

/**
 * Validate that a user has any of the required permissions and throw if not
 */
export function requireAnyPermission(
  userPermissions: AppPermission[],
  requiredPermissions: AppPermission[],
  context?: string
): void {
  if (!hasAnyPermission(userPermissions, requiredPermissions)) {
    throw new AuthorizationError(
      `Missing any of required permissions: ${requiredPermissions.join(", ")}${context ? ` for ${context}` : ""}`,
      requiredPermissions[0], // Use first permission for error
      userPermissions
    );
  }
}

/**
 * Get permissions that a user is missing from a required set
 */
export function getMissingPermissions(
  userPermissions: AppPermission[],
  requiredPermissions: AppPermission[]
): AppPermission[] {
  return requiredPermissions.filter((permission) => !hasPermission(userPermissions, permission));
}

/**
 * Check if user can access a specific route
 */
export function canAccessRoute(
  userPermissions: AppPermission[],
  route: string,
  routePermissions: Record<string, AppPermission[]>
): boolean {
  const requiredPermissions = routePermissions[route];
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No permissions required for this route
  }

  return hasAnyPermission(userPermissions, requiredPermissions);
}

/**
 * Get user's effective role level for comparison
 * Higher numbers indicate more permissions
 */
export function getRoleLevel(appRole: AppRole): number {
  const roleLevels: Record<AppRole, number> = {
    owner: 4,
    admin: 3,
    manager: 2,
    member: 1,
  };

  return roleLevels[appRole] || 0;
}

/**
 * Check if user has at least the specified role level
 */
export function hasMinimumRole(userRole: AppRole, minimumRole: AppRole): boolean {
  return getRoleLevel(userRole) >= getRoleLevel(minimumRole);
}

/**
 * Filter permissions by category for UI display
 */
export function getPermissionsByCategory(
  permissions: AppPermission[]
): Record<string, AppPermission[]> {
  const categories: Record<string, AppPermission[]> = {
    business: [],
    subcontracting: [],
    team: [],
    system: [],
  };

  permissions.forEach((permission) => {
    if (permission === "*") {
      // Add wildcard to all categories
      Object.keys(categories).forEach((category) => {
        categories[category].push(permission);
      });
      return;
    }

    const [domain] = permission.split(":");
    switch (domain) {
      case "leads":
      case "clients":
      case "bids":
      case "contracts":
        categories.business.push(permission);
        break;
      case "opportunities":
      case "applications":
      case "documents":
        categories.subcontracting.push(permission);
        break;
      case "team":
      case "organization":
      case "analytics":
        categories.team.push(permission);
        break;
      case "settings":
      case "audit":
        categories.system.push(permission);
        break;
    }
  });

  return categories;
}

/**
 * Debug helper to log user permissions
 */
export function debugPermissions(userRoleContext: UserRoleContext, label?: string): void {
  if (process.env.NODE_ENV === "development") {
    console.group(`🔐 User Permissions${label ? ` - ${label}` : ""}`);
    console.log("User ID:", userRoleContext.userId);
    console.log("Organization ID:", userRoleContext.organizationId);
    console.log("Clerk Role:", userRoleContext.clerkRole);
    console.log("App Role:", userRoleContext.appRole);
    console.log("Is Owner:", userRoleContext.isOwner);
    console.log("Role Flags:", userRoleContext.roleFlags);
    console.log("Permissions:", userRoleContext.permissions);
    console.groupEnd();
  }
}
